import React, { useState } from 'react';

const TaskCreator = () => {
  // Hardcoded project ID
  const PROJECT_ID = '68832910';
  // Group path for team mentions
  const TEAM_GROUP = 'tw-test-bed/ronnie-team';
  
  const [formState, setFormState] = useState({
    token: '',
    title: '',
    description: '',
    dueDate: '',
    priority: 'medium',
    isLoading: false,
    result: null,
    error: null
  });
  
  // State for group members
  const [groupMembers, setGroupMembers] = useState([]);
  const [groupLoading, setGroupLoading] = useState(false);
  const [groupError, setGroupError] = useState(null);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormState(prev => ({ ...prev, [name]: value }));
    
    // When token changes and isn't empty, fetch group members
    if (name === 'token' && value.trim() !== '') {
      fetchGroupMembers(value);
    }
  };
  
  // Fetch group members using the GitLab API
  const fetchGroupMembers = async (token) => {
    setGroupLoading(true);
    setGroupError(null);
    
    try {
      // First determine if TEAM_GROUP is a group ID or path
      const isNumeric = /^\d+$/.test(TEAM_GROUP);
      
      // Use the standard members endpoint (not /all) to get only direct members
      const groupUrl = isNumeric 
        ? `https://gitlab.com/api/v4/groups/${TEAM_GROUP}/members`
        : `https://gitlab.com/api/v4/groups/${encodeURIComponent(TEAM_GROUP)}/members`;
      
      const response = await fetch(groupUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'PRIVATE-TOKEN': token
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch group members');
      }
      
      const data = await response.json();
      
      // Extract usernames from response
      const members = data
        .filter(member => member.access_level >= 30) // Filter for Developer access and above
        .map(member => member.username);
      
      setGroupMembers(members);
      setGroupLoading(false);
    } catch (err) {
      console.error('Error fetching group members:', err);
      setGroupError(err.message);
      setGroupLoading(false);
    }
  };
  
  const createTask = async (e) => {
    e.preventDefault();
    setFormState(prev => ({ ...prev, isLoading: true, result: null, error: null }));
    
    try {
      // Create notification section with group mention for Slack notification
      const notificationSection = `\n\n### Team Notification\n\n@${TEAM_GROUP}`;
      
      // Create completion section with individual checkboxes from fetched members
      let completionSection = '';
      
      if (groupMembers.length > 0) {
        completionSection = `\n\n### Task Completion\n\n${
          groupMembers.map(member => `- [ ] @${member}`).join('\n')
        }`;
      } else if (groupError) {
        // If group members couldn't be fetched, note the error
        completionSection = `\n\n### Task Completion\n\n*Error loading team members: ${groupError}*`;
      } else {
        // Default message if no members were found
        completionSection = `\n\n### Task Completion\n\n*No team members found with direct access*`;
      }
      
      // Combine description with both sections
      const fullDescription = formState.description + notificationSection + completionSection;
      
      // Create GitLab issue via API
      const apiUrl = `https://gitlab.com/api/v4/projects/${encodeURIComponent(PROJECT_ID)}/issues`;
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'PRIVATE-TOKEN': formState.token
        },
        body: JSON.stringify({
          title: formState.title,
          description: fullDescription,
          due_date: formState.dueDate,
          labels: `Status::Pending,Priority::${formState.priority.charAt(0).toUpperCase() + formState.priority.slice(1)}`
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to create task');
      }
      
      setFormState(prev => ({
        ...prev,
        isLoading: false,
        result: {
          message: 'Task created successfully',
          url: data.web_url
        }
      }));
    } catch (err) {
      setFormState(prev => ({
        ...prev,
        isLoading: false,
        error: err.message
      }));
    }
  };
  
  return (
    <div className="max-w-2xl mx-auto p-6 bg-gray-800 rounded-lg shadow-md text-gray-200">
      {/* Banner image container */}
      <div className="flex justify-center mb-6">
        <img 
          src="/gitlab-logo-dark.png" 
          alt="GitLab Logo" 
          className="h-24 w-auto"
          onError={(e) => {
            // Fallback text if image fails to load
            e.target.style.display = 'none';
            const textNode = document.createElement('div');
            textNode.textContent = 'Team Task Creator';
            textNode.className = 'text-2xl font-bold text-gray-300';
            e.target.parentNode.appendChild(textNode);
          }}
        />
      </div>
      
      <h1 className="text-2xl font-bold mb-6 text-gray-300 text-center">Team Task Creator</h1>
      
      <form onSubmit={createTask} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-300">Personal Access Token</label>
          <input
            type="password"
            name="token"
            value={formState.token}
            onChange={handleChange}
            placeholder="API scope required"
            className="mt-1 block w-full rounded-md border-gray-600 bg-gray-700 text-gray-200 shadow-sm focus:border-gray-500 focus:ring-gray-500 px-4 py-3"
            required
          />
          {groupLoading && (
            <p className="mt-1 text-sm text-blue-400">Loading team members...</p>
          )}
          {groupError && (
            <p className="mt-1 text-sm text-red-400">Error loading team members: {groupError}</p>
          )}
          {groupMembers.length > 0 && (
            <p className="mt-1 text-sm text-green-400">Loaded {groupMembers.length} team members</p>
          )}
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-300">Title</label>
          <input
            type="text"
            name="title"
            value={formState.title}
            onChange={handleChange}
            placeholder=""
            className="mt-1 block w-full rounded-md border-gray-600 bg-gray-700 text-gray-200 shadow-sm focus:border-gray-500 focus:ring-gray-500 px-4 py-3"
            required
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-300">Description</label>
          <textarea
            name="description"
            value={formState.description}
            onChange={handleChange}
            rows="4"
            placeholder=""
            className="mt-1 block w-full rounded-md border-gray-600 bg-gray-700 text-gray-200 shadow-sm focus:border-gray-500 focus:ring-gray-500 px-4 py-3"
            required
          />
        </div>
        
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-gray-300">Due Date</label>
            <input
              type="date"
              name="dueDate"
              value={formState.dueDate}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-600 bg-gray-700 text-gray-200 shadow-sm focus:border-gray-500 focus:ring-gray-500 px-4 py-3"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300">Priority</label>
            <select
              name="priority"
              value={formState.priority}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-600 bg-gray-700 text-gray-200 shadow-sm focus:border-gray-500 focus:ring-gray-500 px-4 py-3"
            >
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </div>
        </div>
        
        <div className="pt-2">
          <button
            type="submit"
            disabled={formState.isLoading}
            className="w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gray-600 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50"
          >
            {formState.isLoading ? 'Creating Task...' : 'Create Task'}
          </button>
        </div>
      </form>
      
      {formState.error && (
        <div className="mt-4 p-4 bg-red-900 border border-red-700 rounded-md">
          <h3 className="text-sm font-medium text-red-300">Error</h3>
          <p className="mt-1 text-sm text-red-200">{formState.error}</p>
        </div>
      )}
      
      {formState.result && (
        <div className="mt-4 p-4 bg-green-900 border border-green-700 rounded-md">
          <h3 className="text-sm font-medium text-green-300">{formState.result.message}</h3>
          {formState.result.url && (
            <p className="mt-1 text-sm">
              <a href={formState.result.url} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-gray-300">
                View task in GitLab
              </a>
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default TaskCreator;